import { QUERY_DEFAULTS } from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { DEFAULT_QUERY_OPTIONS, useInfiniteQueryWithBoundary } from '@/shared/lib/query';
import { assetKeys } from '@/shared/lib/query/queryKeys';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';

import { searchDataAssets } from '@/features/domain/api/dataAssetsApi';

type UseDataAssetsParams = {
  domainFqn: string;
  filters: FilterState;
  query?: string;
  enabled?: boolean;
  sortBy: SortOption['field'];
  sortOrder: SortOption['direction'];
};

export const useDataAssets = ({
  domainFqn,
  filters,
  query = '',
  enabled = true,
  sortBy = 'updatedAt',
  sortOrder = 'desc',
}: UseDataAssetsParams) => {
  return useInfiniteQueryWithBoundary({
    queryKey: assetKeys.dataAssets(domainFqn, filters, query, sortBy, sortOrder),
    queryFn: ({ pageParam = 0 }) =>
      searchDataAssets(
        domainFqn,
        filters,
        query,
        pageParam as number,
        QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
        sortBy,
        sortOrder
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage: OpenMetadataResponse, allPages: OpenMetadataResponse[]) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.hits.hits.length, 0);
      const total = lastPage.hits.total.value;
      return totalFetched < total ? totalFetched : undefined;
    },
    enabled: enabled && !!domainFqn,
    staleTime: 5 * 60 * 1000,
    ...DEFAULT_QUERY_OPTIONS,
  });
};
